import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, LogOut, Menu, X, Settings, BookOpen, Clock3, Users, ListChecks, Home } from 'lucide-react';
import { clearAuthProfile, getStoredAuthProfile, hasAnyPermission, hasPermission } from '../utils/permissions';
import authFetch from '../utils/authFetch';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Responsive State
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle Resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const [taskNotificationCount, setTaskNotificationCount] = useState(0);

  useEffect(() => {
    if (!hasPermission('canMyTime')) {
      return undefined;
    }

    const loadTaskNotifications = async () => {
      try {
        const response = await authFetch(`${API_BASE}/api/tasks/notifications`);
        if (!response.ok) return;
        const payload = await response.json();
        setTaskNotificationCount(Number(payload.newSharedListCount || 0));
      } catch (err) {
        // Silent - notification badge is a non-critical enhancement
      }
    };

    loadTaskNotifications();
    const intervalId = setInterval(loadTaskNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('leadGenUser');
    clearAuthProfile();
    navigate('/login');
  };

  const profile = getStoredAuthProfile();

  // --- STYLES ---
  const containerStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row', // Stack on mobile
    height: '100vh',
    fontFamily: 'Arial, sans-serif'
  };

  const sidebarStyle = {
    width: isMobile ? '100%' : '250px',
    backgroundColor: '#1e293b',
    color: 'white',
    padding: isMobile ? '15px 20px' : '20px',
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    alignItems: isMobile ? 'center' : 'stretch',
    justifyContent: isMobile ? 'space-between' : 'flex-start',
    position: isMobile ? 'relative' : 'static', // Keep static on desktop
    zIndex: 50,
    boxSizing: 'border-box'
  };

  const navContainerStyle = {
    display: isMobile ? (mobileMenuOpen ? 'flex' : 'none') : 'flex',
    flexDirection: 'column',
    gap: '10px',
    // Mobile dropdown styles
    position: isMobile ? 'absolute' : 'static',
    top: isMobile ? '100%' : 'auto',
    left: 0,
    right: 0,
    backgroundColor: '#1e293b',
    padding: isMobile ? '20px' : '0',
    marginTop: isMobile ? '0' : '40px',
    borderTop: isMobile ? '1px solid #334155' : 'none',
    boxShadow: isMobile ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
  };

  return (
    <div style={containerStyle}>
      {/* Sidebar / Topbar */}
      <div style={sidebarStyle}>
        <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '24px' }}>BD Intelligence</h2>
        
        {/* Hamburger Icon (Mobile Only) */}
        {isMobile && (
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
        
        <nav style={navContainerStyle}>
          {hasPermission('canDashboard') && (
            <Link to="/dashboard" style={linkStyle}>
              <LayoutDashboard size={20} /> Dashboard
            </Link>
          )}
          {hasPermission('canRealEstate') && (
            <Link to="/real-estate" style={linkStyle}>
              <Building2 size={20} /> Real Estate
            </Link>
          )}
          {hasPermission('canShortTermRentals') && (
            <Link to="/short-term-rentals" style={linkStyle}>
              <Home size={20} /> Short Term Rentals
            </Link>
          )}
          {hasPermission('canDocumentation') && (
            <Link to="/documentation" style={linkStyle}>
              <BookOpen size={20} /> Documentation
            </Link>
          )}
          {hasPermission('canMyTime') && (
            <Link to="/my-time" style={linkStyle}>
              <Clock3 size={20} /> My Time
            </Link>
          )}
          {hasPermission('canMyTime') && (
            <Link to="/tasks" style={linkStyle}>
              <ListChecks size={20} /> Tasks
              {taskNotificationCount > 0 && (
                <span style={taskBadgeStyle}>{taskNotificationCount > 9 ? '9+' : taskNotificationCount}</span>
              )}
            </Link>
          )}
          {hasPermission('canAdmin') && (
            <Link to="/admin" style={linkStyle}>
              <Settings size={20} /> Admin
            </Link>
          )}
          {hasAnyPermission(['canAdmin', 'canUserManagement']) && (
            <Link to="/admin/users" style={linkStyle}>
              <Users size={20} /> User Management
            </Link>
          )}
          <div style={{ color: '#94a3b8', fontSize: '12px', padding: '8px 12px' }}>
            Signed in as {profile.displayName || profile.username || 'user'}
          </div>
          
          <div style={{ marginTop: isMobile ? '10px' : 'auto', paddingTop: '20px', borderTop: '1px solid #334155' }}>
            <button onClick={handleLogout} style={buttonStyle}>
              <LogOut size={20} /> Logout
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, backgroundColor: '#f1f5f9', overflow: 'auto' }}>
        <div style={{ padding: isMobile ? '20px' : '40px' }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

const linkStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color: '#cbd5e1',
  textDecoration: 'none',
  padding: '12px',
  borderRadius: '8px',
  transition: '0.2s',
};

const taskBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '18px',
  height: '18px',
  padding: '0 5px',
  borderRadius: '999px',
  backgroundColor: '#dc2626',
  color: 'white',
  fontSize: '11px',
  fontWeight: 700,
  lineHeight: '18px'
};

const buttonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  background: 'none',
  border: 'none',
  color: '#cbd5e1',
  cursor: 'pointer',
  fontSize: '16px',
  width: '100%',
  padding: '12px',
  textAlign: 'left'
};

export default Layout;